import { Component, OnInit, ViewChild } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { CookieService } from 'ngx-cookie-service'
import { SeFeDomainEnvironmentService } from 'se-fe-domain-environment'
import { UserServiceResources as us } from 'se-resource-types'
import { DobParser } from '@se-po/shared-data-access-parsers'
import {
  AnalyticsService,
  AuthSessionService,
  InviteService,
  UserServiceCsrfTokenService
} from '@se-po/shared-data-access-services'
import { environment } from '@se-po/shared-environments'
import { StoreService } from '@se-po/se-household-setup-data-access-store'

import { AccountInfoFormCreateService } from './account-info-form-create.service'
import { ACCOUNT_CREATE_FORM_CONFIG } from './form-config'
import { AccountInfoFormSubmitService } from './account-info-form-submit.service'
import { forkJoin } from 'rxjs'

@Component({
  selector: 'se-po-household-setup-account-info',
  templateUrl: './account-info.component.html',
  styleUrls: ['./account-info.component.scss'],
})
export class AccountInfoComponent implements OnInit {

  @ViewChild('emailMatchForm') public emailMatchForm: HTMLFormElement

  public actionUrl: string
  public comfy = true
  public form: UntypedFormGroup
  public formConfig = ACCOUNT_CREATE_FORM_CONFIG
  public ready = false
  public signingIn = false
  public csrfToken: string

  private readonly app = 'SingleSignOn'
  private readonly page = 'SignUp'
  private readonly label = 'Step1'

  constructor(
    private analyticsService: AnalyticsService,
    private authSession: AuthSessionService,
    private env: SeFeDomainEnvironmentService,
    private formCreator: AccountInfoFormCreateService,
    private inviteService: InviteService,
    private route: ActivatedRoute,
    public store: StoreService,
    public submitter: AccountInfoFormSubmitService,
    private cookieService: CookieService,
    private csrfTokenService: UserServiceCsrfTokenService
  ) { }

  public get matchingDupe(): us.UserDuplicateLookupResult {
    return this.submitter.matchingDupe
  }

  public get matchingEmail(): string {
    return this.submitter.matchingEmail
  }

  public set matchingDupe(dupe: us.UserDuplicateLookupResult) {
    this.submitter.matchingDupe = dupe
  }

  public set matchingEmail(email: string) {
    this.submitter.matchingEmail = email
  }

  public ngOnInit(): void {
    if (this.cookieService.get(this.submitter.underAgeCookieName)) {
      this.submitter.agelock = true
    }
    this.createForm()
    this.actionUrl = `${this.env.getUrl('userService')}users/sign_in`
    this.analyticsService.pageView(this.app, this.page, this.label)

    forkJoin({
      auth_session: this.authSession.find(),
      csrf_token: this.csrfTokenService.fetchCsrfToken()
    }).subscribe( ({ auth_session, csrf_token }) => {
      this.csrfToken = csrf_token.csrf_token
      this.initAuthSession(auth_session)
      if (auth_session.invite_token) return this.lookupInvite(auth_session.invite_token)
      this.setReady()
    })
  }

  public emailMatchSignIn(): void {
    this.submitter.submitting = true
    this.analyticsService.seEvent('BackToSignInClick')
    this.emailMatchForm.nativeElement.submit()
  }

  public submit(): void {
    if (this.submitter.agelock) return

    this.submitter.validateAndSubmit(this.form, this.store)
  }

  public initAuthSession(auth_session: us.AuthSession): void {
    this.store.authSession = auth_session
    const email = this.store.authSessionEmailAddress
    if (email) this.form.patchValue({ email })
    else {this.route.queryParams.subscribe(p => {
      if (p.email) this.form.patchValue({ email: p.email })
    })}
  }

  private setReady(): void {
    if (this.store.disableEmail) this.form.get('email').disable()
    this.ready = true
  }

  private lookupInvite(token: string): void {
    this.inviteService.find(token)
      .subscribe(
        (result) => {
          this.store.invite = result
          if (!result?.is_guardian && result?.persona) {
            this.form.patchValue({
              firstName: result.persona.first_name || '',
              lastName: result.persona.last_name || '',
              dateOfBirth: DobParser.isoToMm(result.persona.date_of_birth) || '',
            })
          }
          this.setReady()
        },
        () => {
          // error finding invite, ignore and move on
          this.setReady()
        })
  }

  private createForm(): void {
    this.form = this.formCreator.createForm()
    if (this.store.accountInfo) {
      this.form.patchValue({
        firstName: this.store.accountInfo.firstName,
        lastName: this.store.accountInfo.lastName,
        dateOfBirth: this.store.accountInfo.dateOfBirth,
        email: this.store.accountInfo.email,
        phone: this.store.accountInfo.phone,
        canadian: this.store.accountInfo.canadian,
        thirdPartyMailing: this.store.accountInfo.thirdPartyMailing,
      })
    }
    this.initEmailChange()
  }

  private initEmailChange(): void {
    this.form.get('email').valueChanges
      .subscribe((emailVal: string) => {
        if (this.matchingDupe && emailVal !== this.matchingEmail) {
          this.matchingDupe = undefined
          this.matchingEmail = undefined
        }
      })
  }
}
